// shared/pdf/documents/IncomeExpenseDocument.jsx
import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import { fmtINR } from '../pdfUtils';

const s = StyleSheet.create({
  page:     { fontFamily: 'NotoSans', paddingBottom: 60 },
  content:  { marginHorizontal: 20, marginTop: 10 },
  outer:    { borderWidth: 1, borderStyle: 'solid' },
  cols:     { flexDirection: 'row' },
  colLeft:  { flex: 1, borderRightWidth: 1, borderRightStyle: 'solid' },
  colRight: { flex: 1 },
  colHead:  {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 5,
    borderBottomWidth: 1, borderBottomStyle: 'solid',
  },
  grandRow: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 5,
    borderTopWidth: 2, borderTopStyle: 'solid',
  },
});

// Plain render function (not a React component) — avoids React PDF reconciliation issues
function renderPLRow(row, i, t) {
  if (!row) {
    return <View key={i} style={{ minHeight: 16, paddingHorizontal: 6, backgroundColor: i % 2 === 0 ? t.white : t.light }} />;
  }
  const bg  = row.green ? t.light : row.red ? t.mid : i % 2 === 0 ? t.white : t.light;
  const col = row.green ? t.statusGreen : row.red ? t.statusRed : t.body;
  return (
    <View key={i} style={{ flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 3.5, minHeight: 16, backgroundColor: bg }} wrap={false}>
      <Text style={{ fontSize: 8.5, flex: 1, color: col }}>
        {row.label}
      </Text>
      <Text style={{ fontSize: 8.5, color: col }}>{fmtINR(row.amount)}</Text>
    </View>
  );
}

export default function IncomeExpenseDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];

  const expRows   = Array.isArray(data?.expenditureRows) ? data.expenditureRows : [];
  const incRows   = Array.isArray(data?.incomeRows)      ? data.incomeRows      : [];
  const totalInc  = Number(data?.totalIncome       || 0);
  const totalExp  = Number(data?.totalExpenditure  || 0);
  const netIncome = Number(data?.netIncome         || 0);

  const leftRows  = expRows.map((r) => ({ label: `To  ${r.accountName}`, amount: r.balance ?? 0 }));
  const rightRows = incRows.map((r) => ({ label: `By  ${r.accountName}`, amount: r.balance ?? 0 }));

  if (netIncome > 0) leftRows.push({ label: 'To  Income & Expenditure Account', amount: netIncome, green: true });
  if (netIncome < 0) rightRows.push({ label: 'By  Income & Expenditure Account', amount: Math.abs(netIncome), red: true });

  const balancedTotal = netIncome >= 0 ? totalExp + netIncome : totalInc + Math.abs(netIncome);

  const maxLen = Math.max(leftRows.length, rightRows.length);
  while (leftRows.length  < maxLen) leftRows.push(null);
  while (rightRows.length < maxLen) rightRows.push(null);

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && (
          <PdfHeader derasar={data.derasar} reportTitle="Income & Expenditure Account" theme={t} />
        )}
        <View style={s.content}>
          <View style={[s.outer, { borderColor: t.borderMid }]}>
            <View style={s.cols}>

              {/* Debit / Expenditure */}
              <View style={[s.colLeft, { borderRightColor: t.borderMid }]}>
                <View style={[s.colHead, { backgroundColor: t.dark, borderBottomColor: t.borderMid }]}>
                  <Text style={{ fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1, color: t.headerText }}>Particulars</Text>
                  <Text style={{ fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', color: t.headerText }}>Debit</Text>
                </View>
                {leftRows.map((row, i) => renderPLRow(row, i, t))}
                <View style={[s.grandRow, { backgroundColor: t.mid, borderTopColor: t.borderDark }]}>
                  <Text style={{ fontSize: 9, fontWeight: 'bold', flex: 1, color: t.body }}>Grand Total</Text>
                  <Text style={{ fontSize: 9, fontWeight: 'bold', color: t.body }}>{fmtINR(balancedTotal)}</Text>
                </View>
              </View>

              {/* Credit / Income */}
              <View style={s.colRight}>
                <View style={[s.colHead, { backgroundColor: t.dark, borderBottomColor: t.borderMid }]}>
                  <Text style={{ fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1, color: t.headerText }}>Particulars</Text>
                  <Text style={{ fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', color: t.headerText }}>Credit</Text>
                </View>
                {rightRows.map((row, i) => renderPLRow(row, i, t))}
                <View style={[s.grandRow, { backgroundColor: t.mid, borderTopColor: t.borderDark }]}>
                  <Text style={{ fontSize: 9, fontWeight: 'bold', flex: 1, color: t.body }}>Grand Total</Text>
                  <Text style={{ fontSize: 9, fontWeight: 'bold', color: t.body }}>{fmtINR(balancedTotal)}</Text>
                </View>
              </View>

            </View>
          </View>
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}


